import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useOptimizedUserPreferences } from '@/hooks/useOptimizedUserPreferences';
import { toast } from '@/hooks/use-toast';
import { Trash2, Star, Calendar, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { VoiceTheraPreferences } from './VoiceTheraPreferences';

const getPreferenceTypeIcon = (type: string) => {
  switch (type) {
    case 'voice_gender': return '🎤';
    case 'soundscape': return '🎵';
    case 'background_music': return '🎶';
    case 'session_length': return '⏱️';
    case 'difficulty_level': return '🎯';
    case 'auto_start': return '⚡';
    case 'voicethera_introduction': return '👋';
    case 'voicethera_introduction_style': return '🎭';
    default: return '⚙️';
  }
};

const getPreferenceTypeLabel = (type: string) => {
  switch (type) {
    case 'voice_gender': return 'Voice Selection';
    case 'soundscape': return 'Soundscape';
    case 'background_music': return 'Background Music';
    case 'session_length': return 'Session Length';
    case 'difficulty_level': return 'Difficulty Level';
    case 'auto_start': return 'Auto Start';
    case 'voicethera_introduction': return 'VoiceThera Introduction';
    case 'voicethera_introduction_style': return 'Introduction Style';
    default: return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }
};

const getConfidenceColor = (score: number) => {
  if (score >= 0.8) return 'bg-green-500';
  if (score >= 0.6) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getConfidenceLabel = (score: number) => {
  if (score >= 0.8) return 'High';
  if (score >= 0.6) return 'Medium';
  return 'Low';
};

export const PreferencesList = () => {
  const { preferences, loading } = useOptimizedUserPreferences();

  const handleDeleteAll = async () => {
    // This would require implementing a bulk delete mutation
    toast({
      title: "Feature coming soon",
      description: "Bulk preference deletion will be available in a future update",
    });
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-muted rounded w-1/3"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
                <div className="h-3 bg-muted rounded w-1/4"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!preferences || preferences.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Star className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No preferences saved yet</h3>
          <p className="text-muted-foreground">
            Start using the app to build your personalized preferences automatically
          </p>
        </CardContent>
      </Card>
    );
  }

  const groupedPreferences = preferences.reduce((acc, pref) => {
    if (!acc[pref.category]) {
      acc[pref.category] = [];
    }
    acc[pref.category].push(pref);
    return acc;
  }, {} as Record<string, typeof preferences>);

  return (
    <div className="space-y-6">
      <VoiceTheraPreferences />
      
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Your Saved Preferences</h3>
          <p className="text-sm text-muted-foreground">
            {preferences.length} preference{preferences.length !== 1 ? 's' : ''} across {Object.keys(groupedPreferences).length} categor{Object.keys(groupedPreferences).length !== 1 ? 'ies' : 'y'}
          </p>
        </div>
        
        {preferences.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Trash2 className="h-4 w-4 mr-2" />
                Clear All
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all preferences?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all your saved preferences. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteAll} className="bg-destructive text-destructive-foreground">
                  Delete All
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {Object.entries(groupedPreferences).map(([category, categoryPrefs]) => (
        <div key={category} className="space-y-3">
          <h4 className="text-md font-medium capitalize flex items-center gap-2">
            <Badge variant="outline">{category}</Badge>
            <span className="text-sm text-muted-foreground">
              {categoryPrefs.length} preference{categoryPrefs.length !== 1 ? 's' : ''}
            </span>
          </h4>
          
          <div className="grid gap-3">
            {categoryPrefs.map((pref) => (
              <Card key={pref.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{getPreferenceTypeIcon(pref.preference_type)}</span>
                        <span className="font-medium">{getPreferenceTypeLabel(pref.preference_type)}</span>
                        <Badge 
                          variant="secondary" 
                          className={`text-xs ${getConfidenceColor(pref.confidence_score)} text-white`}
                        >
                          {getConfidenceLabel(pref.confidence_score)}
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-muted-foreground space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Value:</span>
                          <code className="bg-muted px-2 py-1 rounded text-xs">
                            {pref.preference_value}
                          </code>
                        </div>
                        
                        <div className="flex items-center gap-4 text-xs">
                          <div className="flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            <span>Confidence: {Math.round(pref.confidence_score * 100)}%</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>Created: {format(new Date(pref.created_at), 'MMM d, yyyy')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};